/**
 * formatUebertragen.ts — „Format übertragen" (Pinsel) im RichTextEditor.
 *
 * Zwei reine Helfer:
 * - `liesFormat` liest das sichtbare Zeichenformat an einer Stelle im Editor
 *   (Schriftart, Größe, Fett, Kursiv, Unterstrichen, Farbe).
 * - `wendeFormatAn` überträgt so ein Format auf die aktuelle Markierung.
 *
 * Gelesen wird ausschließlich aus dem Markup (Inline-Styles und die alten
 * `<font>`/`<b>`/`<i>`/`<u>`-Elemente), nicht über `getComputedStyle`: Was
 * zählt, ist das, was mit der Mail verschickt wird — und das ist das Markup.
 * Fehlt eine Angabe bis hinauf zur Editor-Wurzel, gilt die Editor-Standardschrift
 * (= Sende-Schrift, siehe `sendBodyFont.ts`).
 *
 * Angewendet wird nur über `execCmd`/`applyFontSize` aus `editorCommands.ts`,
 * damit `document.execCommand` weiter an genau einer Stelle steht. Dabei
 * entstehen nur `<font face>`, `<font style="font-size">`, `<b>/<i>/<u>` und
 * `<font color>` — Markup, das der Server-Sanitizer bereits durchlässt.
 */

import {
    EDITOR_DEFAULT_FONT_STACK,
    EDITOR_DEFAULT_SIZE_PT,
    clearFormat,
    execCmd,
    umhuelleAuswahl,
} from "./editorCommands";
import { eigeneTextknoten, knotenAmCursor, listenpunkteDerAuswahl, setzeListenpunktFormat } from "./listenFormat";
import { aktiveAuswahl, aktivesDokument } from "../../windowManager/browserFenster/fensterRegister";

export type TextFormat = {
    /** Anzeigename (erster Eintrag des Stacks), z. B. für das Schriftart-Dropdown. */
    fontFamily: string;
    /**
     * Vollständiger Font-Stack inkl. Ausweichschriften, z. B.
     * `SpiegelSansUI, "Helvetica Neue", Arial, sans-serif`. Übertragen wird
     * IMMER der Stack: Eine Web-Schrift aus einer eingefügten Webseite ist auf
     * dem Rechner meist nicht installiert — sichtbar ist dann die
     * Ausweichschrift. Nur den ersten Namen zu übertragen, ergab genau den
     * Fehler aus T-44: Am Ziel fehlte die Ausweichschrift, der Browser fiel
     * auf seine Standardschrift (Times) zurück.
     */
    fontStack: string;
    sizePt: number;
    bold: boolean;
    italic: boolean;
    underline: boolean;
    /** `null` = keine Farbe im Markup (Editor-Standard). */
    color: string | null;
};

/** Alte HTML-Skala `<font size="1–7">` → pt (Browser-Standard). */
const FONT_SIZE_SKALA_PT: Record<string, number> = {
    "1": 8, "2": 10, "3": 12, "4": 14, "5": 18, "6": 24, "7": 36,
};

/** Erster Eintrag eines Font-Stacks, ohne Anführungszeichen. */
export function ersteSchrift(stack: string): string {
    const erste = stack.split(",")[0] ?? "";
    return erste.trim().replace(/^["']|["']$/g, "").trim();
}

/** `14pt`, `14.6667px`, `1.2em` … → ganze pt, oder `null`, wenn nicht deutbar. */
export function groesseInPt(wert: string): number | null {
    const m = /^\s*([\d.]+)\s*(pt|px)\s*$/i.exec(wert);
    if (!m) return null;
    const zahl = Number(m[1]);
    if (!Number.isFinite(zahl) || zahl <= 0) return null;
    return Math.round(m[2].toLowerCase() === "px" ? zahl * 0.75 : zahl);
}

const istFett = (gewicht: string): boolean | null => {
    if (!gewicht) return null;
    if (gewicht === "bold" || gewicht === "bolder") return true;
    if (gewicht === "normal" || gewicht === "lighter") return false;
    const n = Number(gewicht);
    return Number.isFinite(n) ? n >= 600 : null;
};

/**
 * Liest das Format an `node` (Text- oder Element-Knoten im Editor).
 *
 * Läuft vom Knoten nach oben bis VOR die Editor-Wurzel; die innerste Angabe
 * gewinnt. Die Wurzel selbst (Editor-Container-Style) und alles außerhalb wird
 * nie gelesen — dafür stehen die Standardwerte.
 */
export function liesFormat(node: Node | null, root: HTMLElement): TextFormat {
    let fontStack: string | null = null;
    let sizePt: number | null = null;
    let bold: boolean | null = null;
    let italic: boolean | null = null;
    let underline: boolean | null = null;
    let color: string | null = null;

    let el: Node | null = node && node.nodeType === Node.ELEMENT_NODE ? node : node?.parentNode ?? null;
    // Liegt der Knoten gar nicht im Editor, gelten die Standardwerte.
    if (!el || !root.contains(el)) el = null;
    const start = el as HTMLElement | null;

    while (el && el !== root && el.nodeType === Node.ELEMENT_NODE) {
        const e = el as HTMLElement;
        const tag = e.tagName.toLowerCase();
        const st = e.style;

        if (fontStack == null) {
            const ff = st?.fontFamily || (tag === "font" ? e.getAttribute("face") ?? "" : "");
            if (ff && ersteSchrift(ff)) fontStack = ff.trim();
        }
        if (sizePt == null) {
            const fs = st?.fontSize ? groesseInPt(st.fontSize) : null;
            if (fs != null) sizePt = fs;
            else if (tag === "font" && e.getAttribute("size")) {
                sizePt = FONT_SIZE_SKALA_PT[e.getAttribute("size")!.trim()] ?? null;
            }
        }
        if (bold == null) {
            const w = istFett(st?.fontWeight ?? "");
            if (w != null) bold = w;
            else if (tag === "b" || tag === "strong" || /^h[1-6]$/.test(tag)) bold = true;
        }
        if (italic == null) {
            const fst = st?.fontStyle ?? "";
            if (fst) italic = fst === "italic" || fst === "oblique";
            else if (tag === "i" || tag === "em") italic = true;
        }
        if (underline == null) {
            const td = `${st?.textDecoration ?? ""} ${st?.textDecorationLine ?? ""}`.trim();
            if (td) underline = td.includes("underline") ? true : td.includes("none") ? false : null;
            if (underline == null && tag === "u") underline = true;
        }
        if (color == null) {
            const c = st?.color || (tag === "font" ? e.getAttribute("color") ?? "" : "");
            if (c) color = c;
        }
        el = e.parentNode;
    }

    // Fehlt eine Angabe im Markup, zählt die berechnete Schrift — aber nur, wenn
    // sie sich von der des Editors selbst unterscheidet (z. B. Überschrift oder
    // Schrift über eine CSS-Klasse). Sonst gilt die Standardschrift.
    if (start && (fontStack == null || sizePt == null)) {
        const hier = berechneterStil(start);
        const wurzel = berechneterStil(root);
        if (fontStack == null && hier?.fontFamily && hier.fontFamily !== wurzel?.fontFamily) {
            fontStack = hier.fontFamily;
        }
        if (sizePt == null && hier?.fontSize && hier.fontSize !== wurzel?.fontSize) {
            sizePt = groesseInPt(hier.fontSize);
        }
    }
    const stack = (fontStack ?? EDITOR_DEFAULT_FONT_STACK).trim();

    return {
        fontFamily: ersteSchrift(stack) || ersteSchrift(EDITOR_DEFAULT_FONT_STACK),
        fontStack: stack,
        sizePt: sizePt ?? EDITOR_DEFAULT_SIZE_PT,
        bold: bold ?? false,
        italic: italic ?? false,
        underline: underline ?? false,
        color,
    };
}

function berechneterStil(el: HTMLElement): CSSStyleDeclaration | null {
    try {
        return typeof window !== "undefined" && window.getComputedStyle ? window.getComputedStyle(el) : null;
    } catch {
        return null;
    }
}

/** Liest das Format am Anfang der aktuellen Auswahl (oder Standardwerte). */
export function liesFormatAnAuswahl(root: HTMLElement): TextFormat {
    const sel = typeof window !== "undefined" ? aktiveAuswahl() : null;
    if (!sel || sel.rangeCount === 0) return liesFormat(null, root);
    const r = sel.getRangeAt(0);
    return liesFormat(knotenAmCursor(r.startContainer, r.startOffset), root);
}

/**
 * Schrift und Größe für die Anzeige in den Dropdowns — am Cursor.
 *
 * Maßgeblich ist das Zeichen am Cursor ({@link knotenAmCursor}), nicht der
 * Container: Steht der Cursor am Ende eines Listenpunkts, ist der Container
 * das `<li>` selbst, und die Schrift steckt im `<font>` darin. Vorher zeigte das
 * Dropdown dort 11, obwohl der Text 36 pt groß war. Gelesen wird über
 * {@link liesFormat}: das Markup bis hinauf zur Wurzel (inkl. Schrift am `<li>`),
 * fehlt dort eine Angabe, die berechnete Schrift (`getComputedStyle`, px → pt).
 */
export function liesAnzeigeFormat(root: HTMLElement): { fontFamily: string; sizePt: number } | null {
    const sel = typeof window !== "undefined" ? aktiveAuswahl() : null;
    if (!sel || sel.rangeCount === 0) return null;
    const r = sel.getRangeAt(0);
    if (!root.contains(r.startContainer)) return null;
    const f = liesFormat(knotenAmCursor(r.startContainer, r.startOffset), root);
    return { fontFamily: f.fontFamily, sizePt: f.sizePt };
}

/**
 * Gleicht frisch angelegte Listenpunkte an ihre Schrift an (nach
 * `insertOrderedList`/`insertUnorderedList`): Ein Punkt mit Text übernimmt die
 * Schrift seines Textes, ein leerer Punkt die eingestellte (`vorgabe`, also
 * was in den Dropdowns steht). Die Angabe kommt nur ans `<li>`, wenn sie von
 * der Standardschrift abweicht — sonst bliebe überall überflüssiges Markup.
 */
export function listenpunkteAngleichen(
    root: HTMLElement | null,
    vorgabe: { fontFamily: string; sizePt: number },
): void {
    if (!root) return;
    const sel = typeof window !== "undefined" ? aktiveAuswahl() : null;
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;
    const standard = ersteSchrift(EDITOR_DEFAULT_FONT_STACK);
    root.querySelectorAll<HTMLElement>("li").forEach((li) => {
        if (!range.intersectsNode(li)) return;
        const texte = eigeneTextknoten(li);
        const f = texte.length
            ? (() => { const x = liesFormat(texte[0], root); return { stack: x.fontStack, name: x.fontFamily, pt: x.sizePt }; })()
            : { stack: vorgabe.fontFamily, name: vorgabe.fontFamily, pt: vorgabe.sizePt };
        setzeListenpunktFormat([li], {
            fontFamily: f.name !== standard ? f.stack : null,
            sizePt: f.pt !== EDITOR_DEFAULT_SIZE_PT ? f.pt : null,
        });
    });
}

const istAktiv = (cmd: string): boolean => {
    try {
        // eslint-disable-next-line no-restricted-syntax
        return typeof document.queryCommandState === "function" && aktivesDokument().queryCommandState(cmd);
    } catch {
        return false;
    }
};

/** Zeichen-Styles, die „Formatierung löschen" entfernt. */
const ZEICHEN_STILE = [
    "font-family", "font-size", "font-weight", "font-style", "color",
    "background", "background-color", "text-decoration", "text-decoration-line",
];

/** Entfernt die genannten Inline-Styles (und passende `<font>`-Attribute) von `el`. */
function entferneStile(el: HTMLElement, props: readonly string[]): void {
    for (const p of props) el.style?.removeProperty(p);
    if (el.tagName === "FONT") {
        if (props.includes("font-family")) el.removeAttribute("face");
        if (props.includes("font-size")) el.removeAttribute("size");
        if (props.includes("color")) el.removeAttribute("color");
    }
    if (el.getAttribute("style")?.trim() === "") el.removeAttribute("style");
}

/** Entfernt die Styles an allen Nachfahren — damit der Style der Hülle gilt. */
function bereinigeNachfahren(huelle: HTMLElement, props: readonly string[]): void {
    huelle.querySelectorAll<HTMLElement>("*").forEach((d) => entferneStile(d, props));
}

/**
 * Überträgt `format` auf die aktuelle Markierung im Editor.
 *
 * Schrift und Größe kommen als Inline-Style auf eigene Hüllen um die
 * Markierung ({@link umhuelleAuswahl}); vorhandene Schrift-/Größenangaben
 * innerhalb der Markierung werden vorher entfernt. So gewinnt das übertragene
 * Format gegen alles, was das Ziel bisher hatte — eigene Spans ebenso wie eine
 * vom Eltern-Block vererbte Schrift.
 *
 * Fett/Kursiv/Unterstrichen sind Umschalter: Sie werden nur ausgelöst, wenn der
 * Zustand der Markierung vom Ziel abweicht — sonst würde ein Pinsel von
 * fettem Text auf fetten Text die Fettung gerade entfernen.
 */
export function wendeFormatAn(root: HTMLElement | null, format: TextFormat): void {
    if (!root) return;
    // Vorher bestimmen: Das Umhüllen verschiebt die Auswahl.
    const lis = listenpunkteDerAuswahl(root);
    for (const h of umhuelleAuswahl(root)) {
        bereinigeNachfahren(h, ["font-family", "font-size"]);
        h.style.fontFamily = format.fontStack;
        h.style.fontSize = `${format.sizePt}pt`;
    }
    // ERST danach: Der Browser räumt beim Umhüllen eine Schrift am <li> ab.
    setzeListenpunktFormat(lis, { fontFamily: format.fontStack, sizePt: format.sizePt });
    if (istAktiv("bold") !== format.bold) execCmd(root, "bold");
    if (istAktiv("italic") !== format.italic) execCmd(root, "italic");
    if (istAktiv("underline") !== format.underline) execCmd(root, "underline");
    if (format.color) execCmd(root, "foreColor", format.color);
}

/** Signatur und zitierter Verlauf: dort räumt „Formatierung löschen" nur auf, wenn die Markierung ganz darin liegt. */
const GESCHUETZT = "[data-kmvs-signature], [data-kmvs-quote]";

/** Liegt der Inhalt von `el` vollständig in `range`? */
function ganzMarkiert(range: Range, el: Element): boolean {
    const r = aktivesDokument().createRange();
    r.selectNodeContents(el);
    return (
        range.compareBoundaryPoints(Range.START_TO_START, r) <= 0 &&
        range.compareBoundaryPoints(Range.END_TO_END, r) >= 0
    );
}

/**
 * „Formatierung löschen": setzt die Markierung auf Times New Roman 11 zurück.
 *
 * 1. `removeFormat` des Browsers (entfernt `<font>`, `<b>/<i>/<u>`, Span-Styles).
 * 2. Was danach noch an Elementen IN der Markierung hängt (Block-Styles eines
 *    eingefügten `<p>`/`<div>`, Hintergrundfarbe …), wird entfernt.
 * 3. Erbt die Markierung noch Zeichenformat von einem nur teilweise markierten
 *    Eltern-Element, bekommt sie eine Hülle mit der Standardschrift, die das
 *    Geerbte überschreibt.
 *
 * Signatur- und Zitat-Container behalten ihre `data-*`-Marker; ihr Inhalt wird
 * nur angefasst, wenn die Markierung ganz darin liegt.
 */
export function formatierungLoeschen(root: HTMLElement | null): void {
    if (!root) return;
    const sel = typeof window !== "undefined" ? aktiveAuswahl() : null;
    const vorher = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
    // Listenpunkte, deren Schrift am <li> hängt (Nummer/Punkt) — vorher bestimmen.
    // In Signatur/Zitat nur, wenn die Markierung ganz darin liegt.
    const c = vorher?.commonAncestorContainer ?? null;
    const imSchutz = !!(c && (c.nodeType === Node.ELEMENT_NODE ? (c as Element) : c.parentElement)?.closest(GESCHUETZT));
    const lis = listenpunkteDerAuswahl(root).filter((li) => imSchutz || !li.closest(GESCHUETZT));
    if (!vorher || sel!.isCollapsed || !root.contains(vorher.commonAncestorContainer)) {
        clearFormat(root);
        setzeListenpunktFormat(lis, { fontFamily: null, sizePt: null });
        return;
    }

    // Signatur/Zitat sichern, die nur TEILWEISE (oder per Strg+A mit) markiert
    // sind: `removeFormat` des Browsers würde ihre Schrift sonst mit abräumen.
    const ankerVorher = vorher.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
        ? (vorher.commonAncestorContainer as Element)
        : vorher.commonAncestorContainer.parentElement;
    const umschliessend = ankerVorher?.closest(GESCHUETZT) ?? null;
    const gesichert = umschliessend
        ? []
        : Array.from(root.querySelectorAll<HTMLElement>(GESCHUETZT))
              .filter((g) => vorher.intersectsNode(g))
              .map((g) => ({ g, html: g.innerHTML }));

    clearFormat(root);
    try {
        nachRemoveFormat(root);
        setzeListenpunktFormat(lis, { fontFamily: null, sizePt: null });
    } finally {
        for (const { g, html } of gesichert) if (g.innerHTML !== html) g.innerHTML = html;
    }
}

/** Schritte 2 und 3 von {@link formatierungLoeschen} — auf der Markierung nach `removeFormat`. */
function nachRemoveFormat(root: HTMLElement): void {
    const sel = aktiveAuswahl();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;

    const anker = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
        ? (range.commonAncestorContainer as Element)
        : range.commonAncestorContainer.parentElement;
    // Liegt die ganze Markierung in Signatur/Zitat, ist das gewollt; sonst
    // bleibt deren Inhalt unangetastet.
    const schutzInnen = anker?.closest(GESCHUETZT) ?? null;
    const istGeschuetzt = (el: Element) => !schutzInnen && !!el.closest(GESCHUETZT);

    // 2. Elemente vollständig in der Markierung bereinigen.
    root.querySelectorAll<HTMLElement>("*").forEach((el) => {
        if (!range.intersectsNode(el) || !ganzMarkiert(range, el) || istGeschuetzt(el)) return;
        entferneStile(el, ZEICHEN_STILE);
    });

    // 3. Geerbtes Zeichenformat von teilweise markierten Vorfahren überschreiben.
    const geerbt = { irgendwas: false, fett: false, kursiv: false, farbe: false };
    const walker = document.createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_TEXT);
    const textKnoten: Node[] = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
        ? [range.commonAncestorContainer]
        : [];
    for (let n = walker.nextNode(); n; n = walker.nextNode()) textKnoten.push(n);
    for (const t of textKnoten) {
        if (!t.textContent?.trim() || !range.intersectsNode(t) || !t.parentElement) continue;
        if (istGeschuetzt(t.parentElement)) continue;
        const d = liesDeklariert(t.parentElement, root);
        geerbt.irgendwas ||= d.irgendwas;
        geerbt.fett ||= d.fett;
        geerbt.kursiv ||= d.kursiv;
        geerbt.farbe ||= d.farbe;
    }
    if (!geerbt.irgendwas) return;
    for (const h of umhuelleAuswahl(root)) {
        // Hüllen in Signatur/Zitat räumt das Zurücksetzen in formatierungLoeschen ab.
        if (istGeschuetzt(h)) continue;
        bereinigeNachfahren(h, ZEICHEN_STILE);
        h.style.fontFamily = EDITOR_DEFAULT_FONT_STACK;
        h.style.fontSize = `${EDITOR_DEFAULT_SIZE_PT}pt`;
        if (geerbt.fett) h.style.fontWeight = "normal";
        if (geerbt.kursiv) h.style.fontStyle = "normal";
        if (geerbt.farbe) h.style.color = "#000000";
    }
}

/** Welches Zeichenformat deklarieren die Vorfahren von `start` (bis vor die Wurzel)? */
function liesDeklariert(start: Element | null, root: HTMLElement) {
    const ergebnis = { irgendwas: false, fett: false, kursiv: false, farbe: false };
    let el: Element | null = start;
    while (el && el !== root && root.contains(el)) {
        const e = el as HTMLElement;
        const tag = e.tagName.toLowerCase();
        const st = e.style;
        if (st?.fontFamily || st?.fontSize || (tag === "font" && (e.getAttribute("face") || e.getAttribute("size")))) {
            ergebnis.irgendwas = true;
        }
        if (istFett(st?.fontWeight ?? "") || tag === "b" || tag === "strong" || /^h[1-6]$/.test(tag)) {
            ergebnis.fett = ergebnis.irgendwas = true;
        }
        if (st?.fontStyle === "italic" || tag === "i" || tag === "em") ergebnis.kursiv = ergebnis.irgendwas = true;
        if (st?.color || (tag === "font" && e.getAttribute("color"))) ergebnis.farbe = ergebnis.irgendwas = true;
        el = e.parentElement;
    }
    return ergebnis;
}
